import { useEffect, useMemo, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { REASON_DETAIL_MIN_LENGTH } from '../constants'

export interface DownloadReason {
  reasonCode: string
  reasonDetail: string
  password: string
}

interface Props {
  open: boolean
  onConfirm: (reason: DownloadReason) => void
  onCancel: () => void
}

/**
 * 비밀번호 규칙 — BE `PasswordValidator`(오프라인 미러): 8자 이상 + 대·소문자·숫자·특수문자 각 1개 이상 + 공백 불가.
 * `isLetterOrDigit` 기준의 "특수문자"를 `[^A-Za-z0-9]` 로 근사하되 공백은 별도로 배제한다
 * (GL01 `consolOutbound` 정규식은 공백을 특수문자로 오판하는 흠이 있어 그대로 베끼지 않는다).
 */
export const isValidDownloadPassword = (password: string): boolean =>
  password.length >= 8 &&
  /[A-Z]/.test(password) &&
  /[a-z]/.test(password) &&
  /\d/.test(password) &&
  /[^A-Za-z0-9\s]/.test(password) &&
  !/\s/.test(password)

/**
 * 엑셀 다운로드 모달 — 사유코드·상세사유(1.0 RGR09/GR05 계열 공통)에 파일 비밀번호 칸을 더한다(D9).
 * 셸(schema.after.json `rgr10DownloadModal`)에는 비밀번호 칸이 없다 — BE 가 서버 생성 암호 zip 이라
 * 이 칸 없이는 다운로드가 400 으로 막힌다. 디자인 이탈이며 PR 설명에 사유를 남긴다.
 *
 * 사유코드 옵션은 `useGetExportCombo`(2.0 `/api/v1/combos` DOWNLOAD_CD)로 받는다 — D7 예외였던 1.0 경로는
 * LOGISPRD-14161 에서 옮겼다.
 */
export function ExcelDownloadModal({ open, onConfirm, onCancel }: Readonly<Props>) {
  const { data: combo } = useGetExportCombo({ noErrorMessage: true })
  const options = useMemo(
    () =>
      (combo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code, idx) => ({
        value: code,
        label: combo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [combo],
  )

  const [reasonCode, setReasonCode] = useState('')
  const [reasonDetail, setReasonDetail] = useState('')
  const [password, setPassword] = useState('')

  useEffect(() => {
    if (!open) return
    setReasonCode('')
    setReasonDetail('')
    setPassword('')
  }, [open])

  const isValid = Boolean(reasonCode) && reasonDetail.trim().length >= REASON_DETAIL_MIN_LENGTH && isValidDownloadPassword(password)

  return (
    <core.Modal
      open={open}
      title="엑셀 다운로드"
      width={520}
      okText="확인"
      cancelText="취소"
      okButtonProps={{ disabled: !isValid }}
      onOk={() => isValid && onConfirm({ reasonCode, reasonDetail: reasonDetail.trim(), password })}
      onCancel={onCancel}
    >
      <core.Form layout="vertical" style={{ marginTop: 16 }}>
        <core.Form.Item label="사유코드" required>
          <core.Radio.Group options={options} value={reasonCode} onChange={(e) => setReasonCode(String(e.target.value))} />
        </core.Form.Item>
        <core.Form.Item label="상세사유" required help={`${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해주세요.`}>
          <core.Input autoComplete="off" value={reasonDetail} placeholder="사유를 입력하세요" onChange={(e) => setReasonDetail(e.target.value)} />
        </core.Form.Item>
        <core.Form.Item label="파일 비밀번호" required help="8자 이상, 대문자·소문자·숫자·특수문자를 모두 포함해야 합니다(공백 불가).">
          <core.Input.Password autoComplete="new-password" value={password} placeholder="압축 파일 비밀번호" onChange={(e) => setPassword(e.target.value)} />
        </core.Form.Item>
      </core.Form>
    </core.Modal>
  )
}
